import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Search, PersonSearch } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import characters from "../characters.json";

function PickerContent({
  accentColor,
  currentCharacter,
  filteredCharacters,
  isMobile,
  onCharacterSelect,
  search,
  onSearchChange,
  selectedIndex,
}) {
  return (
    <Stack
      spacing={2}
      sx={{
        width: isMobile ? "100%" : { xs: 320, sm: 520 },
        maxWidth: "100%",
        p: isMobile ? 0 : 2,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          pb: 1.5,
          background: (theme) => theme.palette.background.paper,
        }}
      >
        <TextField
          autoFocus
          fullWidth
          label="Search sticker"
          helperText="输入角色名或贴纸编号，快速筛选"
          value={search}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          alt={currentCharacter.name}
          src={`/img/${currentCharacter.img}`}
          variant="rounded"
          sx={{
            width: 52,
            height: 52,
            borderRadius: 1.5,
            border: (theme) =>
              `1px solid ${theme.palette.outline?.accent ?? alpha(accentColor, 0.3)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {currentCharacter.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sticker #{currentCharacter.id} · 当前选中贴纸
          </Typography>
        </Box>
      </Stack>

      {filteredCharacters.length > 0 ? (
        <ImageList
          cols={isMobile ? 2 : 4}
          gap={12}
          rowHeight={isMobile ? 156 : 140}
          sx={{
            m: 0,
            pb: 0.5,
            maxHeight: isMobile ? "calc(100vh - 240px)" : 468,
          }}
        >
          {filteredCharacters.map(({ character, index }) => {
            const selected = index === selectedIndex;

            return (
              <ImageListItem
                key={`${character.id}-${character.img}`}
                onClick={() => onCharacterSelect(index)}
                sx={{
                  cursor: "pointer",
                  overflow: "hidden",
                  borderRadius: 2,
                  border: (theme) =>
                    `1px solid ${
                      selected
                        ? theme.palette.outline?.accent ??
                          alpha(accentColor, 0.36)
                        : theme.palette.outline?.main ??
                          alpha("#ffffff", 0.12)
                    }`,
                  boxShadow: selected
                    ? `0 12px 28px ${alpha(accentColor, 0.22)}`
                    : "none",
                  transition:
                    "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 12px 24px ${alpha(accentColor, 0.18)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: (theme) => theme.palette.surface?.elevated,
                  }}
                >
                  <Box
                    component="img"
                    src={`/img/${character.img}`}
                    alt={character.name}
                    loading="lazy"
                    sx={{
                      flexGrow: 1,
                      width: "100%",
                      objectFit: "contain",
                      backgroundColor: alpha("#ffffff", 0.03),
                    }}
                  />
                  <Box sx={{ px: 1.25, py: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {character.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      #{character.id} · {character.character}
                    </Typography>
                  </Box>
                </Box>
              </ImageListItem>
            );
          })}
        </ImageList>
      ) : (
        <Box
          sx={{
            px: 2,
            py: 4,
            borderRadius: 2,
            textAlign: "center",
            border: (theme) => `1px dashed ${theme.palette.outline?.main}`,
            backgroundColor: (theme) => theme.palette.surface?.elevated,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            No stickers found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            换个角色名、编号，或者试试更短的关键词。
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

export default function Picker({ value, onChange, accentColor }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");

  const currentCharacter = characters[value];
  const open = Boolean(anchorEl);

  const filteredCharacters = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return characters.map((character, index) => ({ character, index }));
    }

    return characters
      .map((character, index) => ({ character, index }))
      .filter(({ character }) => {
        return (
          character.id === keyword ||
          character.name.toLowerCase().includes(keyword) ||
          character.character.toLowerCase().includes(keyword)
        );
      });
  }, [search]);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch("");
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
  }, []);

  const handleCharacterSelect = useCallback(
    (nextIndex) => {
      onChange(nextIndex);
      handleClose();
    },
    [handleClose, onChange]
  );

  const content = (
    <PickerContent
      accentColor={accentColor}
      currentCharacter={currentCharacter}
      filteredCharacters={filteredCharacters}
      isMobile={isMobile}
      onCharacterSelect={handleCharacterSelect}
      onSearchChange={handleSearchChange}
      search={search}
      selectedIndex={value}
    />
  );

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={
          <Avatar
            alt={currentCharacter.name}
            src={`/img/${currentCharacter.img}`}
            sx={{ width: 24, height: 24 }}
          />
        }
        endIcon={<PersonSearch />}
        sx={{
          alignSelf: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          color: "text.primary",
          borderColor: (theme) => theme.palette.outline?.accent,
          backgroundColor: (theme) => theme.palette.tonal?.soft,
          px: 1.5,
          minWidth: { xs: "100%", sm: 236 },
        }}
      >
        <Stack alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Typography variant="button" noWrap>
            {currentCharacter.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Change sticker / 切换贴纸
          </Typography>
        </Stack>
      </Button>

      {isMobile ? (
        <Dialog fullScreen open={open} onClose={handleClose}>
          <DialogTitle>Sticker picker / 选择贴纸</DialogTitle>
          <DialogContent dividers>{content}</DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1.5,
              borderRadius: 3,
            },
          }}
        >
          {content}
        </Popover>
      )}
    </>
  );
}
